// features/workstations/catalyst/methodology-section.js
// Methodology section — current version, change log entries,
// library version, links to canonical docs in repo.

import { useEffect, useState } from 'react';

const REPO_BLOB_BASE = 'https://github.com/timmai1004-ship-it/shift-prototype/blob/main';

const KEY_DOCS = [
  {
    label: 'Framework',
    path: 'docs/CATALYST_RESEARCH_FRAMEWORK.md',
    desc: '4-pillar methodology: pre-registration / tracking / stress test / change management',
  },
  {
    label: 'Build doc (active)',
    path: 'docs/PENNY_CATALYST_SUSTAIN_BUILD.md',
    desc: 'v2.0 vertical spec: universe, 5-band feature schema, 11-phase build sequence',
  },
  {
    label: 'Reframe record',
    path: 'docs/catalyst/REFRAME_2026-05-07.md',
    desc: 'Why v1.x → v2.0. Greenlight history. Lessons captured.',
  },
  {
    label: 'Catalyst Language Library',
    path: 'docs/catalyst/CATALYST_LANGUAGE_LIBRARY.md',
    desc: 'Theory-driven library (v2.3: ~389 phrases; biotech + tech/SaaS + M&A + financial deepened + crypto-adjacent new, 8 sectors covered). L3 feature dictionary.',
  },
  {
    label: 'Literature reference',
    path: 'docs/catalyst/LITERATURE.md',
    desc: 'Loughran-McDonald / Tetlock / Engelberg / Cohen-Malloy-Nguyen / Hassan et al. + practitioner conventions.',
  },
  {
    label: 'Change log',
    path: 'docs/catalyst/CHANGE_LOG.md',
    desc: 'Methodology version history. v1.0 → v1.1 → v2.0 → v2.1 → v2.2. Non-retroactive.',
  },
];

export default function MethodologySection({ userId }) {
  const [data, setData] = useState({ changes: [], loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
        const res = await fetch(`/api/workstations/catalyst/list-change-log${qs}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const payload = await res.json();
        if (cancelled) return;
        setData({ changes: payload.changes || [], loading: false, error: null });
      } catch (err) {
        if (!cancelled) setData((s) => ({ ...s, loading: false, error: err.message }));
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  return (
    <div className="cat-section">
      <div className="cat-block">
        <div className="cat-block-title">Operative methodology</div>
        <div className="cat-method-version-pill">catalyst-v2.2</div>
        <div className="cat-block-subtitle">
          Amended 2026-05-08 (SUSTAIN OUTCOME volume threshold corrected from
          0.7× D+0 spike volume to 1.5× pre-event 30-day ADV — the v2.1 condition
          was mechanically broken; spike volume is unreachable at D+30 because
          interest normalizes within days). Universe: penny-cap stocks ($50M-$500M,
          $1-$20) experiencing D+0 catalyst-driven pump (≥+50% intraday on
          confirmed news). Outcome: D+30 sustain (close ≥+25% above pre-pump AND
          D+30 volume ≥1.5× pre-event ADV). 5-band feature schema. 65% sustain-rate
          validation threshold with 95% CI lower bound {'>'} 55%.
        </div>
      </div>

      <div className="cat-block">
        <div className="cat-block-title">Key documents</div>
        <div className="cat-block-subtitle">
          The framework, build doc, and supporting artifacts. All locked, all versioned.
          Open in GitHub to read.
        </div>
        <div className="cat-hypothesis-list">
          {KEY_DOCS.map((d) => (
            <a
              key={d.path}
              href={`${REPO_BLOB_BASE}/${d.path}`}
              target="_blank"
              rel="noreferrer"
              className="cat-hyp-row"
              style={{ textDecoration: 'none' }}
            >
              <div className="cat-hyp-row-left">
                <div className="cat-hyp-code">{d.label}</div>
                <div className="cat-hyp-meta">
                  <span className="cat-hyp-vertical">{d.desc}</span>
                </div>
              </div>
              <div className="cat-hyp-row-right">
                <span className="cat-hyp-doc-link">open ↗</span>
              </div>
            </a>
          ))}
        </div>
      </div>

      <div className="cat-block">
        <div className="cat-block-title">Methodology version history</div>
        <div className="cat-block-subtitle">
          Each entry is immutable. Predictions made under v(N) are forever scored under v(N) —
          no retroactive recalibration. Per Pillar 4B Rule 1 of the framework.
        </div>
        {data.loading && <div className="cat-loading">Loading change log…</div>}
        {data.error && <div className="cat-error">Could not load change log: {data.error}</div>}
        {!data.loading && !data.error && (
          <div className="cat-changelog-list">
            {data.changes.length === 0 ? (
              <div className="cat-empty">No change log entries found. Apply <code>catalyst-research-migration.sql</code> in Supabase.</div>
            ) : (
              data.changes.map((c) => <ChangeLogRow key={c.version} entry={c} />)
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ChangeLogRow({ entry }) {
  return (
    <div className="cat-changelog-row">
      <div className="cat-changelog-version">{entry.version}</div>
      <div className="cat-changelog-date">
        {entry.effective_at ? new Date(entry.effective_at).toISOString().slice(0, 10) : '—'}
        {entry.author && <span style={{ marginLeft: 12 }}>· {entry.author}</span>}
      </div>
      <div className="cat-changelog-what">{entry.what_changed}</div>
      {entry.why && <div className="cat-changelog-why">Why: {entry.why}</div>}
    </div>
  );
}
